"use client";

import YouTubeVideoOverlay from "@/components/atoms/YouTubeVideoOverlay";
import {
  // [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → central widget_impression; see webapp/docs/analytics
  // FireWidgetViewedEvent,
  VideoButtonClick,
  VideoModalClosed,
  iconClick,
} from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import Image from "next/image";
import Link from "next/link";
import { useRef, useState } from "react";

const MobileApp1 = ({ data }: { data: any }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);

  const isMobile = useIsMobile(800);

  // Handle double nesting from PreviewRenderer (same pattern as other components)
  const sectionData = (data as any)?.data || data;

  if (!sectionData?.status) {
    return null;
  }

  const {
    google_play_icon = "",
    google_play_icon_alt = "",
    google_play_link = "",
    app_store_icon = "",
    app_store_icon_alt = "",
    app_store_link = "",
    alt_image = "",
    desktop_bg_image = "",
    mobile_bg_image = "",
    video_link = "",
    title = "",
    title_text_color = "#000000",
    background_color = "#000000",
    is_blog: isBlogRaw,
  } = sectionData || {};

  const isBlogLayout =
    isBlogRaw === true || isBlogRaw === 1 || isBlogRaw === "1";

  // Track widget viewed
  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "mobile_app_section");
  //   }
  // }, []);

  // Handle video button click
  const handleVideoClick = () => {
    if (video_link) {
      VideoButtonClick({
        label: title || "mobile_app_video",
      });
      setIsOpen(true);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    VideoModalClosed({
      label: title || "mobile_app_video",
    });
    setIsOpen(false);
  };

  // Handle Google Play link click
  const handleGooglePlayClick = () => {
    iconClick({
      section: "mobile_app",
      component: "app_store_links",
      entity: "google_play",
      landing_url: google_play_link,
      landing_page_key: "",
    });
  };

  // Handle App Store link click
  const handleAppStoreClick = () => {
    iconClick({
      section: "mobile_app",
      component: "app_store_links",
      entity: "app_store",
      landing_url: app_store_link,
      landing_page_key: "",
    });
  };

  return (
    <>
      <section
        className={cn(
          "flex",
          isBlogLayout
            ? "w-full min-w-0 max-w-full justify-start items-stretch"
            : "w-full justify-center items-center",
        )}
        style={{ backgroundColor: background_color || "#000000" }}
      >
        <div
          className={cn(
            "flex flex-col w-full",
            "xs:gap-6",
            isBlogLayout
              ? "max-w-none px-0 sm:px-0 sm:pt-6 sm:pb-6 sm:gap-6 sm:items-start"
              : cn(
                  "px-5 pb-8 sm:pt-[42px] sm:pb-[42px] sm:w-full sm:px-9 sm:gap-6 sm:items-center",
                  "sm:w-[1008px] md:w-[1158px] lg:w-[1358px] xl:w-[1692px]",
                ),
          )}
        >
          <div
            className={cn(
              "flex flex-col w-full",
              "xs:gap-5",
              "sm:gap-[10px]",
              "md:gap-3",
              "lg:gap-3",
              "xl:gap-6",
              isBlogLayout && "items-start",
            )}
          >
            <h2
              className={cn(
                isBlogLayout ? "w-full text-left" : "text-center",
                isBlogLayout
                  ? "font-bold text-[24px] leading-snug sm:text-[26px] md:text-[28px] lg:text-[30px] xl:text-[32px]"
                  : cn(
                      "xs:text-heading-6-semibold",
                      "sm:text-heading-5-semibold",
                      "md:text-heading-4-semibold",
                      "lg:text-new-heading-34-semibold",
                      "xl:text-new-heading-42-semibold",
                    ),
                "line-clamp-2",
                "type-h3",
              )}
              style={{ color: title_text_color }}
            >
              {title}
            </h2>
            {(google_play_link || app_store_link) && (
              <div
                className={cn(
                  "flex items-center",
                  isBlogLayout ? "justify-start" : "justify-center",
                  "xs:gap-3",
                  "sm:gap-[6px]",
                  "md:gap-2",
                  "lg:gap-2",
                  "xl:gap-2",
                )}
              >
                {google_play_link && google_play_icon && (
                  <Link
                    href={google_play_link}
                    target="_blank"
                    onClick={handleGooglePlayClick}
                  >
                    <Image
                      src={google_play_icon}
                      alt={google_play_icon_alt || "Get it on Google Play"}
                      className="w-[95px] h-[29px]"
                      width={95}
                      height={29}
                    />
                  </Link>
                )}

                {app_store_link && app_store_icon && (
                  <Link
                    href={app_store_link}
                    target="_blank"
                    onClick={handleAppStoreClick}
                  >
                    <Image
                      src={app_store_icon}
                      alt={app_store_icon_alt || "Download on the App Store"}
                      className="w-[95px] h-[29px]"
                      width={95}
                      height={29}
                    />
                  </Link>
                )}
              </div>
            )}
          </div>
          {(mobile_bg_image || desktop_bg_image) && (
            <div
              className={cn(
                "relative w-full overflow-hidden rounded-[24px] sm:rounded-[40px]",
                {
                  "cursor-pointer": video_link,
                },
              )}
              onClick={handleVideoClick}
            >
              {(mobile_bg_image || desktop_bg_image) && (
                <Image
                  src={
                    isMobile
                      ? mobile_bg_image || desktop_bg_image
                      : desktop_bg_image || mobile_bg_image
                  }
                  alt={alt_image || "Mobile App"}
                  // Banner is a single self-contained image — show it whole
                  // (no crop), filling the section width at its natural ratio.
                  className="h-auto w-full object-contain"
                  width={1280}
                  height={531}
                  sizes="100vw"
                />
              )}
            </div>
          )}
        </div>
      </section>
      {video_link && (
        <YouTubeVideoOverlay
          isOpen={isOpen}
          onClose={handleModalClose}
          videoUrl={video_link}
        />
      )}
    </>
  );
};

export default MobileApp1;
